<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div class="new-object-page">
    <loading-spinner v-if="this.$apollo.loading" class="new-object-page__loading" />
    <div v-else-if="error">There was an error: {{ error }}</div>
    <div v-else class="content-wrapper">
      <h1 v-if="isInsideModal" class="new-object-page__title">{{ pageTitle }}</h1>
      <dynamic-form
        ref="dynamicForm"
        :object-type="currentModelMeta.modelSingular"
        :attributes="attributes"
        :is-creating="isCreating"
        @createObject="createObject"
        @cancel="$emit('close')"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import LoadingSpinner from '@reciprocity/loading-spinner';
import { normalizeNewObject } from './utils';
import DynamicForm from './components/DynamicForm';
import * as customObjectAttributes from './custom_object_attributes';

export default {
  name: 'NewObjectPage',
  components: {
    DynamicForm,
    LoadingSpinner,
  },
  props: {
    objectType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      newObject: '',
      error: null,
      isCreating: false,
    };
  },
  computed: {
    ...mapGetters(['modelMeta', 'routes']),
    currentModelMeta() {
      return this.modelMeta[this.objectType];
    },
    pageTitle() {
      return `New ${this.currentModelMeta.titleSingular}`;
    },
    attributes() {
      if (this.newObjectData) {
        const currentCustomObjectAttributes = customObjectAttributes[this.objectType] || [];
        return this.newObjectData.attrs.concat(currentCustomObjectAttributes);
      }
      return [];
    },
    objectMutationQuery() {
      const mutationQueries = {
        request: require('@/graphql/new_object/mutations/CreateNewRequest.gql'),
        task: require('@/graphql/new_object/mutations/CreateNewTask.gql'),
        assessment: require('@/graphql/new_object/mutations/CreateNewAssessment.gql'),
      };
      return mutationQueries[this.objectType];
    },
    isInsideModal() {
      return this.$store.state.modals.modals.some(modal => modal.type === 'NewObjectPage');
    },
  },
  created() {
    if (!this.isInsideModal) {
      const backLink = this.getBackLinkObject();
      this.setPageTitle({ title: this.pageTitle, backLink });
    }
  },
  methods: {
    ...mapActions({
      setPageTitle: 'setPageTitle',
      showAlert: 'alerts/show',
      confirmModal: 'modals/confirm',
    }),
    async createObject({ regularAttributes, customAttributes, keepModalOpened }) {
      this.isCreating = true;
      try {
        const newObject = await this.$apollo.mutate({
          mutation: this.objectMutationQuery,
          variables: {
            input: regularAttributes,
            customAttributes,
          },
        });
        const dataKey = `create${this.currentModelMeta.modelSingular}`;
        const newObjectId = newObject.data[dataKey].result.dbId;
        if (keepModalOpened) {
          this.$refs.dynamicForm.cleanForm();
          this.showObjectCreatedMessage(newObjectId);
          this.scrollToTheTop();
        } else {
          if (this.isInsideModal) {
            this.showObjectCreatedMessage(newObjectId);
            this.confirmModal();
          } else {
            this.redirectToObjectInfoPage(newObjectId);
          }
        }
      } catch (error) {
        this.setFieldErrorsOnForm(error);
      }
      this.isCreating = false;
    },
    getBackLinkObject() {
      if (document.referrer) {
        return { label: 'Back', url: new URL(document.referrer).pathname };
      }
      return false;
    },
    redirectToObjectInfoPage(objectId) {
      window.location.href = this.getObjectInfoURL(objectId);
    },
    showObjectCreatedMessage(objectId) {
      const href = this.getObjectInfoURL(objectId);
      this.showAlert({
        type: 'success',
        message: `New <a href="${href}">${this.currentModelMeta.modelSingular} with ID ${objectId}</a> successfully created.`,
      });
    },
    getObjectInfoURL(objectId) {
      return this.routes.sorInfo
        .replace('__OBJECT_TYPE__', this.currentModelMeta.modelSingular)
        .replace('__OBJECT_ID__', objectId)
        .replace('__TAB__', 'info');
    },
    scrollToTheTop() {
      const container = document.querySelector('.modal') || document.querySelector('.container-new-object');
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    },
    setFieldErrorsOnForm(responseError) {
      if (responseError.graphQLErrors && responseError.graphQLErrors.length) {
        const { setFieldError } = this.$refs.dynamicForm;
        responseError.graphQLErrors.forEach(({ extensions }) => {
          extensions.errors.forEach(error => {
            if (error.attribute_names) {
              setFieldError(error.attribute_names[0], error.user_message);
            } else {
              this.showGeneralError(error.user_message);
              // TODO: TEMP throw error instead
              // eslint-disable-next-line no-console
              console.error(responseError);
            }
          });
        });
      } else {
        this.showGeneralError();
      }
    },
    showGeneralError(message) {
      this.showAlert({
        type: 'error',
        title: `${this.currentModelMeta.titleSingular} could not be created.`,
        message,
      });
    },
  },
  apollo: {
    newObjectData: {
      query: require('@/graphql/new_object/NewObject.gql'),
      variables() {
        return { type: this.currentModelMeta.modelSingular };
      },
      error(error) {
        this.error = JSON.stringify(error.message);
      },
      update({ newObjectData }) {
        return normalizeNewObject(newObjectData);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/design/colors';

.new-object-page {
  // The 90px is the height of the 'page-top' used in the App template
  min-height: calc(100vh - 90px);
  display: flex;
  flex-direction: column;
  background-color: $color-white;

  &__loading {
    flex-grow: 1;
  }

  &__title {
    font-weight: bold;
    font-size: 1.25rem;
    min-height: 50px;
    display: flex;
    align-items: center;
    padding-left: 1.5rem;
  }

  .content-wrapper {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
}
</style>
